import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, Search, Filter, MapPin, Calendar, Briefcase, 
  DollarSign, Clock, ChevronRight, Star, Users, Mic
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

// Sample casting calls data
const CASTING_CALLS = [
  {
    id: 1,
    title: "Lead Actor - Drama Series",
    project: "The Rising Star",
    category: "Actor",
    type: "TV Series",
    location: "Los Angeles, CA",
    compensation: "$5,000 - $10,000/episode",
    deadline: "Feb 15, 2024",
    description: "Looking for a male lead actor (25-35) for an upcoming drama series about a struggling musician.",
    requirements: ["Age 25-35", "Experience in drama", "Strong emotional range"],
    status: "open",
    applicants: 45
  },
  {
    id: 2,
    title: "Fashion Model - Spring Campaign",
    project: "Luxe Fashion Brand",
    category: "Model",
    type: "Commercial",
    location: "New York, NY",
    compensation: "$2,500/day",
    deadline: "Jan 28, 2024",
    description: "High-end fashion brand seeking diverse models for spring 2024 campaign.",
    requirements: ["Height 5'8\"+", "Professional portfolio", "Available for 3-day shoot"],
    status: "open",
    applicants: 120
  },
  {
    id: 3,
    title: "Voice Artist - Animation",
    project: "Kids Animated Series",
    category: "Voice Artist",
    type: "Animation",
    location: "Remote",
    compensation: "$500/episode",
    deadline: "Feb 5, 2024",
    description: "Seeking voice actors for various characters in a new animated series.",
    requirements: ["Voice demo reel", "Character voices", "Available for ongoing work"],
    status: "open",
    applicants: 78
  },
  {
    id: 4,
    title: "Background Dancer - Music Video",
    project: "Pop Artist MV",
    category: "Dancer",
    type: "Music Video",
    location: "Miami, FL",
    compensation: "$800/day",
    deadline: "Jan 25, 2024",
    description: "High-energy dancers needed for upcoming pop music video.",
    requirements: ["Hip-hop/contemporary style", "Available Jan 28-29", "Professional experience"],
    status: "closing_soon",
    applicants: 56
  },
  {
    id: 5,
    title: "Singer - Studio Recording",
    project: "Album Feature",
    category: "Singer",
    type: "Recording",
    location: "Nashville, TN",
    compensation: "$3,000 + royalties",
    deadline: "Feb 10, 2024",
    description: "R&B/Soul singer needed for album feature track with established artist.",
    requirements: ["Strong vocal range", "R&B experience", "Professional recording experience"],
    status: "open",
    applicants: 34
  }
];

export default function CastingCall() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedCall, setSelectedCall] = useState(null);

  const filteredCalls = CASTING_CALLS.filter(call => {
    const matchesSearch = !searchTerm || 
      call.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      call.project.toLowerCase().includes(searchTerm.toLowerCase()) ||
      call.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === "all" || 
      call.category.toLowerCase() === selectedCategory.toLowerCase();
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
                <span className="font-display text-xl font-bold text-gray-900">Casting Calls</span>
              </div>
            </div>
            <Badge variant="outline" className="hidden sm:flex">
              <Mic className="w-3 h-3 mr-1" /> {CASTING_CALLS.length} Open Calls
            </Badge>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-pink-600 to-violet-600 text-white py-10 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-display text-3xl font-bold mb-4">Find Your Next Opportunity</h1>
          <p className="text-pink-100 max-w-2xl mb-6">
            Browse through casting calls from production companies, agencies, and brands looking for talented individuals like you.
          </p>
          
          {/* Search */}
          <div className="flex flex-col sm:flex-row gap-3 max-w-3xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title, project, or location..."
                className="pl-10 h-12 bg-white text-gray-900 border-0"
                data-testid="search-input"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-48 h-12 bg-white text-gray-900 border-0" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="actor">Actor</SelectItem>
                <SelectItem value="model">Model</SelectItem>
                <SelectItem value="singer">Singer</SelectItem>
                <SelectItem value="dancer">Dancer</SelectItem>
                <SelectItem value="voice artist">Voice Artist</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Listings */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-600">{filteredCalls.length} casting calls found</p>
            </div>

            {filteredCalls.map(call => (
              <Card 
                key={call.id}
                className={`cursor-pointer transition-all hover:shadow-md ${selectedCall?.id === call.id ? 'ring-2 ring-violet-500' : ''}`}
                onClick={() => setSelectedCall(call)}
                data-testid={`casting-call-${call.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{call.title}</h3>
                        {call.status === "closing_soon" && (
                          <Badge className="bg-yellow-100 text-yellow-800 text-xs">Closing Soon</Badge>
                        )}
                      </div>
                      <p className="text-violet-600 text-sm font-medium">{call.project}</p>
                    </div>
                    <Badge variant="secondary">{call.category}</Badge>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> {call.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" /> {call.compensation}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> Deadline: {call.deadline}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm line-clamp-2">{call.description}</p>
                  
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                    <span className="text-gray-500 text-xs flex items-center gap-1">
                      <Users className="w-3 h-3" /> {call.applicants} applicants
                    </span>
                    <Button variant="ghost" size="sm" className="text-violet-600">
                      View Details <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {filteredCalls.length === 0 && (
              <Card className="p-12 text-center">
                <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">No casting calls found</h3>
                <p className="text-gray-500">Try adjusting your search or filters</p>
              </Card>
            )}
          </div>

          {/* Selected Call Details */}
          <div className="lg:col-span-1">
            {selectedCall ? (
              <Card className="sticky top-24">
                <CardHeader>
                  <Badge className="w-fit mb-2">{selectedCall.type}</Badge>
                  <CardTitle className="font-display">{selectedCall.title}</CardTitle>
                  <p className="text-violet-600 font-medium">{selectedCall.project}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" /> {selectedCall.location}
                    </p>
                    <p className="flex items-center gap-2 text-gray-600">
                      <DollarSign className="w-4 h-4" /> {selectedCall.compensation}
                    </p>
                    <p className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" /> Deadline: {selectedCall.deadline}
                    </p>
                    <p className="flex items-center gap-2 text-gray-600">
                      <Users className="w-4 h-4" /> {selectedCall.applicants} applicants
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                    <p className="text-gray-600 text-sm">{selectedCall.description}</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Requirements</h4>
                    <ul className="space-y-1">
                      {selectedCall.requirements.map((req, i) => (
                        <li key={i} className="text-gray-600 text-sm flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-1.5" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button className="w-full btn-primary" data-testid="apply-btn">
                    Apply Now
                  </Button>
                  <p className="text-center text-xs text-gray-500">
                    <Badge variant="outline">Demo Mode</Badge> Applications coming soon
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="p-8 text-center sticky top-24">
                <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Select a Casting Call</h3>
                <p className="text-gray-500 text-sm">Click on a listing to view details</p>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
